import React, { lazy, useEffect, useRef, useState } from "react";
import { InstallationSummaryGrid } from "./InstallationSummaryGrid";
import { OrderShippingSummaryGrid } from "./OrderShippingSummaryGrid";
import { INSTALLATION_SUMMARY_LIST_COLS } from "../../constants/InstallationSummaryConstants";
import { getOrderSummaryList } from "../../services/C4S Decommission - Replacement/getOrderSummaryList";
import { SLNotification, Spacer } from "../../components/core/utility";
import styled from "styled-components";
import { generate } from "shortid";
import useOutsideClick from "../../customHook/use-outside-click";
import { Loader } from "@vds/loaders";

const CustomNotification = styled(SLNotification)`
  & div {
    justify-content: flex-start;
    max-width: 100%;
    & span:first-child {
      flex: 2 1 0%;
    }
  }
`;

const SummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
`;

const SummaryTitle = styled.h3`
  font-family: "Verizon NHG eDS", Arial, Helvetica, sans-serif;
  font-weight: 700;
  font-style: bold;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0px;
  margin: 2px 0 0 0;
  width: 60%;
`;

const FulfillmentTickets = styled.span`
  margin: 10px 0 0 0;
  width: 40%;
  font-weight: 400;
  font-size: 12px;
`;

const OrderSummary = () => {
  const [installationList, setInstallationList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [orderSummaryList, setOrderSummaryList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const hasRedirectedRef = useRef(false);
  const cspSessionData = JSON.parse(sessionStorage.getItem("CSPDetails"));
  const providerOrder = cspSessionData?.CSP?.length ? cspSessionData.CSP.find((csp) => csp.DefaultCSP === "1")?.Provider_Order : "";

  const handleClickOutside = () => setNotification(null);
  const clickRef = useOutsideClick(handleClickOutside);

  useEffect(() => {
    getOrderSummary();
  }, []);

  const getOrderSummary = async () => {
    const userAccountId = cspSessionData?.AccountID;
    const userId = sessionStorage?.getItem("userId");
    const cspId = cspSessionData?.CSP?.length ? cspSessionData?.CSP?.find((csp) => csp?.DefaultCSP === "1")?.CSPID : "";

    if (userAccountId && userId && cspId && providerOrder) {
      try {
        setIsLoading(true);
        const payload = { accountId: userAccountId, userId, id: cspId, order: providerOrder };
        const response = await getOrderSummaryList(payload);
        if (response.status === 200 && response.data?.data) {
          const rawBundles = response?.data?.data?.orderSummary?.bundles || [];
          const filteredBundles = rawBundles
            .map((b) => ({
              ...b,
              summaryLines: (b?.summaryLines || []).filter((line) => (line?.mmidType || "").toLowerCase() !== "service"),
            }))
            .filter((b) => b?.summaryLines?.length > 0);
          setOrderSummaryList(filteredBundles);
          const data = response.data.data?.orderSummary?.bundles
            .map((bundle) => ({
              ...bundle,
              summaryLines: bundle.summaryLines.filter((line) => line.serviceName !== "NA"),
            }))
            .filter((bundle) => bundle.summaryLines.length > 0);

          setInstallationList(data);
        } else {
          notify("We are currently experiencing issues, please try again", "error", false);
        }
      } catch (e) {
        notify("We are currently experiencing issues, please try again", "error", false);
      } finally {
        setIsLoading(false);
        setLoaded(true);
      }
    } else {
      setLoaded(true);
    }
  };

  const notify = (content, type, isNotificationWithTimer = true) => {
    setNotification(
      <>
        <CustomNotification key={generate()} type={type} title={content} fullBleed={true} ref={clickRef} />
        <Spacer bottom="10px" />
      </>
    );
    if (isNotificationWithTimer) {
      setTimeout(() => setNotification(null), 5000);
    }
  };

  return (
    <div>
      {notification}
      <Loader active={isLoading} fullscreen />
      <>
        <SummaryHeader>
          <SummaryTitle>Order Shipping Summary</SummaryTitle>
          <FulfillmentTickets>Fulfillment Tickets</FulfillmentTickets>
        </SummaryHeader>
        <Spacer top=".5rem" />
        <OrderShippingSummaryGrid rows={orderSummaryList} />
      </>
      <>
        <Spacer top="1rem" />
        <SummaryTitle>Order Installation Summary</SummaryTitle>
        <hr />
        <Spacer top="1rem" />
        <InstallationSummaryGrid rows={installationList} columns={INSTALLATION_SUMMARY_LIST_COLS} />
      </>
    </div>
  );
};
export default OrderSummary;

export const OrderSummaryMetadata = {
  name: "Order Summary",
  id: "order_summary",
  component: lazy(() => import("./OrderSummary")),
  route: "/order_summary",
};
